import { Download } from "lucide-react";
import { toast } from "sonner";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { download, errMsg, fmtL } from "@/lib/api";
import { PageHeader, Stat, Spinner, Badge } from "@/components/opl/Common";
import { useList, DataTable, GhostBtn } from "@/components/opl/Crud";

const Ex = ({ path, name }) => (
  <div className="flex gap-2">{["csv", "xlsx", "pdf"].map((f) => <GhostBtn key={f} testid={`export-${name}-${f}`} onClick={() => download(`${path}?fmt=${f}`, `${name}.${f}`).catch((e) => toast.error(errMsg(e)))}><Download className="h-4 w-4" />{f.toUpperCase()}</GhostBtn>)}</div>
);

export default function Reports() {
  const [s] = useList("/reports/summary");
  const [teams] = useList("/reports/teams");
  const [players] = useList("/reports/players");
  const [rec] = useList("/reports/reconcile");
  if (!s) return <Spinner />;
  return (
    <div className="space-y-10">
      <PageHeader title="Reports & Analytics" crumbs={["Reports"]}>
        {rec && <Badge testid="reconcile-badge" className={rec.balanced ? "text-emerald-300 border-emerald-500/40" : "text-red-300 border-red-500/40"}>{rec.balanced ? "Ledger reconciled" : "Ledger mismatch"} · sales {fmtL(rec.sales_total)} · ledger {fmtL(rec.ledger_total)}</Badge>}
      </PageHeader>
      <section>
        <div className="flex justify-between items-center mb-3"><p className="eyebrow">Auction summary</p><Ex path="/reports/summary" name="auction_summary" /></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Registered" value={s.total_players} /><Stat label="Sold" value={s.sold} /><Stat label="Unsold" value={s.unsold} /><Stat label="Remaining" value={s.remaining} />
          <Stat label="Total spent" value={fmtL(s.total_spent)} accent testid="report-total-spent" /><Stat label="Unspent purse" value={fmtL(s.total_unspent)} />
          <Stat label="Highest purchase" value={s.highest ? fmtL(s.highest.price) : "—"} sub={s.highest ? `${s.highest.player} → ${s.highest.team}` : ""} /><Stat label="Average price" value={fmtL(s.average)} />
        </div>
      </section>
      <section>
        <div className="flex justify-between items-center mb-3"><p className="eyebrow">Team-wise</p><Ex path="/reports/teams" name="team_report" /></div>
        {teams && (
          <div className="surface rounded-xl p-4 h-64 mb-4">
            <ResponsiveContainer><BarChart data={teams.map((t) => ({ name: t.short_name || t.name, spent: t.spent, remaining: t.remaining_purse }))}>
              <XAxis dataKey="name" stroke="#64748B" fontSize={11} /><YAxis stroke="#64748B" fontSize={11} />
              <Tooltip contentStyle={{ background: "#0B1323", border: "1px solid #334155" }} />
              <Bar dataKey="spent" stackId="a" fill="#E5B842" /><Bar dataKey="remaining" stackId="a" fill="#1E3358" />
            </BarChart></ResponsiveContainer>
          </div>
        )}
        <DataTable rows={teams} testid="team-report-table" columns={[
          { key: "name", label: "Team", render: (r) => <b>{r.name}</b> }, { key: "ip", label: "Start", render: (r) => <span className="num">{fmtL(r.initial_purse)}</span> },
          { key: "sp", label: "Spent", render: (r) => <span className="num">{fmtL(r.spent)}</span> }, { key: "rp", label: "Remaining", render: (r) => <span className="num">{fmtL(r.remaining_purse)}</span> },
          { key: "sq", label: "Squad", render: (r) => r.players.length }, { key: "roles", label: "By role", render: (r) => <span className="text-xs text-slate-400">{Object.entries(r.roles).map(([k, v]) => `${k.split("-")[0]} ${v}`).join(" · ")}</span> },
          { key: "pl", label: "Purchases", render: (r) => <span className="text-xs">{r.players.map((p) => `${p.name} (${fmtL(p.purchase_price)})`).join(", ") || "—"}</span> },
        ]} />
      </section>
      <section>
        <div className="flex justify-between items-center mb-3"><p className="eyebrow">Player-wise</p><Ex path="/reports/players" name="player_report" /></div>
        <DataTable rows={players} testid="player-report-table" columns={[
          { key: "code", label: "Code", render: (r) => <span className="num text-xs">{r.code}</span> }, { key: "name", label: "Player" }, { key: "category", label: "Category" },
          { key: "base_price", label: "Base", render: (r) => <span className="num">{fmtL(r.base_price)}</span> }, { key: "final_price", label: "Final", render: (r) => <span className="num">{fmtL(r.final_price)}</span> },
          { key: "team", label: "Team" }, { key: "bids", label: "Bids" }, { key: "status", label: "Status" },
        ]} />
      </section>
    </div>
  );
}
